/**
 * @fileoverview Reusable orientation banner that introduces a page's purpose.
 * Shows for the first N visits, then auto-expires. Dismissible via close button.
 * Persists state in localStorage.
 *
 * @example
 *   const banner = new OrientationBanner({
 *     container: this.container.querySelector('.share-content'),
 *     key: 'discovery',
 *     message: 'This is where you explore topics about your relationship.',
 *     maxViews: 5,
 *     insertPosition: 'beforebegin'
 *   });
 *   banner.show();
 */

const STORAGE_PREFIX = 'orientation';

// Temporary global kill switch for first-visit page guidance. Keep the
// component and its persisted view state intact so the banners can be
// restored without changing each page that uses them.
const ORIENTATION_BANNERS_ENABLED = false;

export class OrientationBanner {
  /**
   * @param {Object} config
   * @param {HTMLElement} config.container - Element to insert the banner relative to
   * @param {string} config.key - Unique page key for localStorage (e.g. 'discovery')
   * @param {string} config.message - Orientation message to display
   * @param {number} [config.maxViews=5] - Number of views before auto-expiry
   * @param {string} [config.insertPosition='beforebegin'] - insertAdjacentElement position
   * @param {string} [config.linkText='Learn more'] - Optional link text appended after message
   * @param {Function} [config.onLinkClick] - Callback when link is clicked
   */
  constructor(config) {
    this.container = config.container;
    this.key = config.key;
    this.message = config.message;
    this.maxViews = config.maxViews ?? 5;
    this.insertPosition = config.insertPosition ?? 'beforebegin';
    this.linkText = config.linkText ?? null;
    this.onLinkClick = config.onLinkClick ?? null;
    this.el = null;
  }

  /** @returns {boolean} Whether the banner should still appear. */
  _shouldShow() {
    if (localStorage.getItem(`${STORAGE_PREFIX}-${this.key}-dismissed`)) return false;
    const views = parseInt(localStorage.getItem(`${STORAGE_PREFIX}-${this.key}-views`) || '0', 10);
    return views < this.maxViews;
  }

  _incrementViews() {
    const k = `${STORAGE_PREFIX}-${this.key}-views`;
    const views = parseInt(localStorage.getItem(k) || '0', 10);
    localStorage.setItem(k, String(views + 1));
  }

  _dismiss() {
    localStorage.setItem(`${STORAGE_PREFIX}-${this.key}-dismissed`, '1');
    this._animateOut();
  }

  _animateOut() {
    if (!this.el) return;
    this.el.style.maxHeight = this.el.scrollHeight + 'px';
    requestAnimationFrame(() => {
      this.el?.classList.add('orientation-banner--hiding');
    });
    this.el.addEventListener('transitionend', () => {
      this.el?.remove();
      this.el = null;
    }, { once: true });
  }

  _buildElement() {
    const el = document.createElement('div');
    el.className = 'orientation-banner';
    el.setAttribute('role', 'status');

    const linkHtml = this.linkText
      ? ` <a href="#" class="orientation-banner__link">${this.linkText}</a>`
      : '';

    el.innerHTML = `
      <p class="orientation-banner__message">${this.message}${linkHtml}</p>
      <button class="orientation-banner__close" aria-label="Dismiss orientation">
        <svg width="14" height="14"><use href="#icon-close"/></svg>
      </button>
    `;

    el.querySelector('.orientation-banner__close')
      .addEventListener('click', () => this._dismiss());

    const link = el.querySelector('.orientation-banner__link');
    if (link && this.onLinkClick) {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.onLinkClick();
      });
    }

    return el;
  }

  show() {
    if (!ORIENTATION_BANNERS_ENABLED) return;
    if (!this.container || !this._shouldShow()) return;

    this._incrementViews();
    this.el = this._buildElement();
    this.container.insertAdjacentElement(this.insertPosition, this.el);
  }

  destroy() {
    this.el?.remove();
    this.el = null;
  }
}
